import Section from "@/pages/settings/components/sections/Section";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import ReasonListSection from "@/pages/settings/components/shared/reasons/ReasonListSection";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  useAccountSettings,
  useUpdateAccountSettingByKey
} from "@/entities/settings/hooks";
import {
  useClosedAsQualifiedReasonsSettings,
  useCreateClosedAsQualifiedReason,
  useCreateDisqualifiedReason,
  useDeleteClosedAsQualifiedReason,
  useDeleteDisqualifiedReason,
  useDisqualifiedReasonsSettings,
  useUpdateClosedAsQualifiedReason,
  useUpdateClosedAsQualifiedReasonsOrder,
  useUpdateDisqualifiedReason,
  useUpdateDisqualifiedReasonsOrder
} from "@/entities/settings/lead/hooks";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import { resolveBooleanAccountSetting } from "@/entities/settings/accountSettingDefaults";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import ConfirmIcpModal, { ConfirmIcpModalMode } from "./ConfirmIcpModal";
import { DisqualifiedReasons } from "@/pages/leads/types";

const TRANSLATION_KEY_TABS = "settings.leads.tabs";
const DISQUALIFIED_REASONS_PREFIX = `${TRANSLATION_KEY_TABS}.generalTab.disqualifiedReasons`;
const CLOSED_AS_QUALIFIED_REASONS_PREFIX = `${TRANSLATION_KEY_TABS}.generalTab.closedAsQualifiedReasons`;

// UNQUALIFIED_COMPANY and UNQUALIFIED_PERSON trigger a dedicated mini-flow that
// references them by stable ID — editing their labels would break the flow UX,
// so editing is disabled for these entries (deletion is still allowed).
const PROTECTED_DISQUALIFIED_REASON_IDS = [
  DisqualifiedReasons.UNQUALIFIED_COMPANY,
  DisqualifiedReasons.UNQUALIFIED_PERSON
];
const PROTECTED_DISQUALIFIED_REASON_EDIT_TOOLTIP_KEY = `${DISQUALIFIED_REASONS_PREFIX}.protectedEditTooltip`;

export const GeneralTab: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ConfirmIcpModalMode>("disable");

  const { enabled: showLeadReasonLists } = useFeatureFlag(FeatureFlags.CRM_7464_LEAD_REASON_LISTS);

  const accountSettingsQuery = useAccountSettings();
  const accountSettings = accountSettingsQuery.data;
  const updateSettingsByKey = useUpdateAccountSettingByKey();

  const disqualifiedReasonsQuery = useDisqualifiedReasonsSettings({ enabled: showLeadReasonLists });
  const createDisqualifiedReason = useCreateDisqualifiedReason();
  const updateDisqualifiedReason = useUpdateDisqualifiedReason();
  const deleteDisqualifiedReason = useDeleteDisqualifiedReason();
  const updateDisqualifiedReasonsOrder = useUpdateDisqualifiedReasonsOrder();

  const closedAsQualifiedReasonsQuery = useClosedAsQualifiedReasonsSettings({ enabled: showLeadReasonLists });
  const createClosedAsQualifiedReason = useCreateClosedAsQualifiedReason();
  const updateClosedAsQualifiedReason = useUpdateClosedAsQualifiedReason();
  const deleteClosedAsQualifiedReason = useDeleteClosedAsQualifiedReason();
  const updateClosedAsQualifiedReasonsOrder =
    useUpdateClosedAsQualifiedReasonsOrder();

  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};

  const isChecked = resolveBooleanAccountSetting(
    "confirm_icp_process",
    keyValuePairs?.confirm_icp_process
  );

  const handleCheckedChange = (checked: boolean) => {
    setModalMode(checked ? "enable" : "disable");
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    updateSettingsByKey.mutate({
      pathParams: { settingKey: "confirm_icp_process" },
      data: {
        value: modalMode === "enable"
      }
    });
  };

  return (
    <>
      {showLeadReasonLists && (
        <>
          <ReasonListSection
            tPrefix={DISQUALIFIED_REASONS_PREFIX}
            droppableId="lead-disqualified-reasons"
            serverReasons={
              disqualifiedReasonsQuery.data?.disqualifiedReasons ?? []
            }
            refetch={disqualifiedReasonsQuery.refetch}
            isCreating={createDisqualifiedReason.isPending}
            protectedEditIds={PROTECTED_DISQUALIFIED_REASON_IDS}
            protectedEditTooltip={t(PROTECTED_DISQUALIFIED_REASON_EDIT_TOOLTIP_KEY)}
            onCreate={(name) =>
              createDisqualifiedReason.mutateAsync({ data: { name } })
            }
            onEdit={(id, name) =>
              updateDisqualifiedReason.mutateAsync({
                pathParams: { reasonId: id },
                data: { name }
              })
            }
            onDelete={(id) =>
              deleteDisqualifiedReason.mutateAsync({
                pathParams: { reasonId: id },
                data: {}
              })
            }
            onReorder={(orderedIds) =>
              updateDisqualifiedReasonsOrder.mutateAsync({ data: orderedIds })
            }
          />
          <ReasonListSection
            tPrefix={CLOSED_AS_QUALIFIED_REASONS_PREFIX}
            droppableId="lead-closed-as-qualified-reasons"
            serverReasons={
              closedAsQualifiedReasonsQuery.data?.closedAsQualifiedReasons ?? []
            }
            refetch={closedAsQualifiedReasonsQuery.refetch}
            isCreating={createClosedAsQualifiedReason.isPending}
            onCreate={(name) =>
              createClosedAsQualifiedReason.mutateAsync({ data: { name } })
            }
            onEdit={(id, name) =>
              updateClosedAsQualifiedReason.mutateAsync({
                pathParams: { reasonId: id },
                data: { name }
              })
            }
            onDelete={(id) =>
              deleteClosedAsQualifiedReason.mutateAsync({
                pathParams: { reasonId: id },
                data: {}
              })
            }
            onReorder={(orderedIds) =>
              updateClosedAsQualifiedReasonsOrder.mutateAsync({ data: orderedIds })
            }
          />
        </>
      )}
      <Section
        title={t(`${TRANSLATION_KEY_TABS}.generalTab.leadWorkflow.title`)}
        subTitle={t(
          `${TRANSLATION_KEY_TABS}.generalTab.leadWorkflow.subTitle`
        )}
        items={
          [
            {
              title: t(
                `${TRANSLATION_KEY_TABS}.generalTab.leadWorkflow.confirmIcp.title`
              ),
              subTitle: t(
                `${TRANSLATION_KEY_TABS}.generalTab.leadWorkflow.confirmIcp.subTitle`
              ),
              subTitleBreakAfterSentence: true,
              actionType: "switch",
              actionProps: {
                checked: isChecked,
                onCheckedChange: handleCheckedChange
              }
            }
          ] as SettingItemLayoutProps[]
        }
      />
      <ConfirmIcpModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        mode={modalMode}
        onConfirm={handleConfirm}
      />
    </>
  );
};
